/** The hero sheet: portrait, primaries, secondaries, the artifact doll, the
 * army, and the road to the next level.
 *
 * Laid out as two halves rather than three columns: the left is the hero
 * himself (portrait, speciality, experience, what he has left to spend) and the
 * right is what he is equipped with and commands.
 *
 * Three things on this sheet are structural rather than decorative and are worth
 * stating, because each of them was a defect first:
 *
 * 1. **The doll is a lattice and a tray, not one long grid.** The nine slots
 *    that are worn on the body are laid on a 3x3 lattice in the order a player
 *    puts armour on - collar row, arm row, hem row. The nine that are carried
 *    rather than worn (five miscellaneous, four war machines) sit in a bordered
 *    tray *below* it, so "equipped" and "in the pack" are two objects on the
 *    plate rather than eighteen identical holes. There is no figure drawn
 *    behind the lattice; see the note above `FIGURE_LAYOUT` for why.
 * 2. **Artifact art is drawn 1:1.** Artifact icons are 128px at source, and the
 *    integer-scale rule means the only sizes available are 128 and 64. At 64 the
 *    thing the player hunts the whole map for was the smallest art object on the
 *    sheet. The sockets are sized to hold 128.
 * 3. **The army stands on one ground line.** A creature frame is trimmed and
 *    carries its ground line in `anchorY`, and every frame in a faction sheet
 *    shares an *untrimmed* height. Scaling by the trimmed box - which is what
 *    fitting into a square box does - gives a mounted Lancer a smaller divisor
 *    than an Angel and inverts the rank. The divisor is taken from `srcH`, so it
 *    is the same for every stack, and each sprite is seated by its anchor so the
 *    seven pairs of feet land on one line.
 *
 * Dragging an artifact issues `EQUIP_ARTIFACT`; a slot that would refuse the
 * drop is greyed and carries the kernel's own reason as its tooltip, so the
 * screen never invents a rule. Dragging a stack splits it, and shift-drag moves
 * exactly one creature - The classic's gesture, and the only one that lets a player
 * leave a single unit behind to hold a mine.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type {
  ArmyState, ArtifactDef, ArtifactDefId, ContentRegistry, CreatureDef, GameCommand,
  HeroDef, HeroDefId, HeroState,
  Modifier, PrimarySkills, Reason, SecondarySkillDef, SecondarySkillState, SpellDefId,
  SkillDefId, SubskillDefId,
} from "@game/core";
import {
  FP_ONE, SKILL_CAP_FP, SKILL_STEP_FP, assemblableFor,
  creatureBonusesFor, disassembleIllegality, effectiveSkillLevel, pickedSubskills,
  skillLevel, skillLevelBonus, spellSpecialtyFp, subskillRung,
} from "@game/core";
import {
  BitmapText, fitBitmapText, useBitmapScale, useDevicePixelRatio,
} from "../theme/bitmapText.js";
import { attachRefs, usePhoneSheetFit } from "../theme/phone.js";
import { PRIMARY_ICON, PRIMARY_LABEL, PRIMARY_ORDER, uiIconKey } from "../theme/art.js";
import type { Atlas } from "../theme/atlas.js";
import { useAtlas } from "../hooks/useAtlas.js";
import { artifactIcon, creatureIcon, heroPortrait, spellIcon } from "./art.js";
import { MASTERY_NAME, compactCount, groupDigits, titleCase } from "./format.js";
import { ART_ICON, ART_ICON_LARGE, ReasonLine } from "./parts.js";
import { usePolishStyles } from "./polish.js";
import { MaybeIcon, ScreenDialog, ScreenStyles, useMeasure } from "./primitives.js";
import { abilityPhrases, creatureStatLines } from "./creatureCard.js";
import {
  DOLL, armyDropCommand, carryTap, equipIllegality, experienceBar, fortuneWord, heroFortune,
  receivingSlot, unequipIllegality,
} from "./heroModel.js";
import type { Carry } from "./heroModel.js";
import { SplitDialog } from "./town/dialogs.js";
import { explainReason } from "./townModel.js";
import { velvetVars } from "./townChrome.js";
import {
  ARMY_COUNT_SCALE, ARMY_COUNT_SCALE_COMPACT, ARMY_GROUND, ARMY_GROUND_COMPACT, ARMY_PLINTH,
  ARMY_PLINTH_COMPACT, DIAL_ICON, DOLL_CELL, DOLL_CELL_COMPACT, MOOD_ICON_SIZE, PACK_CELL,
  PACK_CELL_COMPACT, PACK_CELL_MIN, PACK_CELL_MIN_COMPACT, PACK_COLS, PACK_GAP, PACK_GAP_COMPACT,
  PACK_WHEEL_DEAF_MS, PORTRAIT_COMPACT, PRIM_ICON, PRIM_ICON_COMPACT, SKILL_ICON,
  SKILL_ICON_COMPACT, useHeroStyles,
} from "./heroScreen.css.js";
export { BUTTONS_W, BUTTONS_W_COMPACT, HERO_CSS, HERO_GEOMETRY } from "./heroScreen.css.js";

const PRIMARY_ICON_BY_KEY: Readonly<Record<string, string>> = PRIMARY_ICON;

/* Movement and mana have no icon of their own in the shipped set; these are the
   two that name the thing rather than decorate it - the logistics boot and the
   mysticism vessel. */
const DIAL_ICON_KEY: Readonly<Record<string, string>> = Object.freeze({
  movement: "ui.adventure.logistics",
  mana: "ui.magic.mysticism",
  experience: "ui.adventure.scholar",
});

/* The word over each primary medallion, the reference's own: it prints
   "Power", not "Spell Power", because the cell is a medallion wide. */
const PRIMARY_SHORT: Readonly<Record<string, string>> = Object.freeze({
  attack: "Attack",
  defense: "Defense",
  spellPower: "Power",
  knowledge: "Knowledge",
});

/* The same four words for a cell that cannot hold them.
 *
 * A bitmap caption cannot be squeezed - the floor holds its face and its
 * magnification, and scale 1 is already the bottom - so a word wider than its
 * plate does not shrink, it hangs off the side. Measured by `qa/clipping.mjs`
 * before this existed: "Knowledge" painted 8.8px past the left rail of its
 * plaque at 1440x900 and at 1366x880, and nowhere else - those are the two
 * windows tall enough to miss the compact set and narrow enough to squeeze the
 * column. The other three words fit at every size; only this row's widest
 * decides, and when it has to give way all four give way together, because a
 * row reading "Attack / Defense / Power / Know" with three full words and one
 * clipped is what a broken plate looks like. */
const PRIMARY_TIGHT: Readonly<Record<string, string>> = Object.freeze({
  attack: "Att",
  defense: "Def",
  spellPower: "Pow",
  knowledge: "Know",
});

/** Grid gap plus the plate's own padding and rails, per cell. The widest of the
 *  three stylesheets that dress this row, so the fit is never optimistic. */
const PRIM_CELL_GAP_PX = 7;
const PRIM_CELL_CHROME_PX = 8;

/* Morale and luck DO have their own art, three faces each, and picking the
   face by the reading is the whole point of them: the classic's morale icon is a
   different drawing at -1 and at +2, so the dial says which way the number
   goes before the number is read. */
const MOOD_ICON = (kind: "morale" | "luck", value: number): string =>
  `ui.mood.${kind}_${value > 0 ? "high" : value < 0 ? "low" : "neutral"}`;

/**
 * The figure under the sockets, in the plate's own coordinates.
 *
 * This is the thing the doll was missing, and the reason an EMPTY the classic doll
 * still reads as a person: the plate is not a grid of holes, it is a leather
 * board with a knight painted on it and the holes cut through him. The lattice
 * is five columns by six rows and the socket centres land at 7.9, 28.9, 50,
 * 71.1 and 92.1 per cent across and 7.9, 28.9, 50, 71, 92 and 113 down a
 * 100x121 box, so the helm sits under the crown socket, the hands under the
 * two outboard sockets at the ends of the arms, the rings at hip height either
 * side of the cape and the boots at the hem. Rows five and six - the
 * miscellaneous shelf and the war machines - fall below the figure, which is
 * where the classic puts things that are carried rather than worn.
 *
 * A previous pass drew this as a 1-2px hairline armature and it out-ranked
 * every real element on the panel. It is a FILL now, at low alpha under the
 * sockets, with a thin keyline only where the silhouette turns - so what a
 * player sees through the gutters is a shape, not a drawing.
 */
const FIGURE_POINTS = [
  /* crown, temples, jaw */
  "50,1", "56,2", "59,8", "59,15", "56,19",
  /* right shoulder and a tapering arm out to the hand socket */
  "64,21", "71,25", "75,32",
  "84,40", "93,47", "96,52", "90,58", "83,51", "76,45",
  /* right flank, waist, and the cape falling behind the leg */
  "73,51", "71,59", "69,67",
  "76,77", "78,89", "74,99",
  /* hem, then up between the boots and down again */
  "64,99", "61,85", "57,80", "50,79", "43,80", "39,85", "36,99",
  "26,99", "22,89", "24,77",
  "31,67", "29,59", "27,51",
  /* left arm, mirrored */
  "24,45", "17,51", "10,58", "4,52", "7,47", "16,40",
  "25,32", "29,25", "36,21",
  "44,19", "41,15", "41,8", "44,2",
].join(" ");

/**
 * The words a player uses for a `Modifier`'s stat, and what its integer means.
 *
 * `flat` is the number itself, `pct` a percentage already in percent, and `bp`
 * basis points out of `BP_ONE`. Only the units differ - the kernel keeps
 * probabilities in basis points and rates in percent, and a panel that printed
 * "+3000 eagleEye" would be reading the storage rather than the rule.
 *
 * Labels are lower case and short on purpose: they are read inline after the
 * number ("+25% damage"), and the plaque that shows one has about 26 characters
 * of room at 1366x768.
 */
interface StatWords {
  readonly label: string;
  readonly unit: "flat" | "pct" | "bp";
}

const SKILL_STAT: Readonly<Record<string, StatWords>> = Object.freeze({
  attack: { label: "attack", unit: "flat" },
  defense: { label: "defense", unit: "flat" },
  defenseSiege: { label: "siege defense", unit: "flat" },
  spellPower: { label: "spell power", unit: "flat" },
  knowledge: { label: "knowledge", unit: "flat" },
  speed: { label: "speed", unit: "flat" },
  hp: { label: "hit points", unit: "flat" },
  morale: { label: "morale", unit: "flat" },
  luck: { label: "luck", unit: "flat" },
  moraleEnemy: { label: "enemy morale", unit: "flat" },
  luckEnemy: { label: "enemy luck", unit: "flat" },
  /* Offense and Archery both carry damageMin and damageMax, so both keys read
     as one line and the pair collapses to a single chip. Which damage it is -
     melee or ranged - is the description's job; the stat key does not say. */
  damageMin: { label: "damage", unit: "flat" },
  damageMax: { label: "damage", unit: "flat" },
  shots: { label: "shots", unit: "flat" },
  movement: { label: "movement", unit: "flat" },
  movementSea: { label: "sea movement", unit: "flat" },
  scouting: { label: "scouting radius", unit: "flat" },
  mana: { label: "spell points", unit: "flat" },
  manaRegen: { label: "spell points a day", unit: "flat" },
  spellDamage: { label: "spell damage", unit: "flat" },
  spellDuration: { label: "spell duration", unit: "flat" },
  spellCost: { label: "spell cost", unit: "flat" },
  spellPowerEnemy: { label: "enemy spell power", unit: "flat" },
  experienceGain: { label: "experience", unit: "flat" },
  growth: { label: "creature growth", unit: "flat" },
  "income.gold": { label: "gold a day", unit: "flat" },
  "income.wood": { label: "wood a day", unit: "flat" },
  "income.ore": { label: "ore a day", unit: "flat" },
  "income.mercury": { label: "mercury a day", unit: "flat" },
  "income.sulfur": { label: "sulfur a day", unit: "flat" },
  "income.crystal": { label: "crystal a day", unit: "flat" },
  "income.gems": { label: "gems a day", unit: "flat" },
  stealth: { label: "stealth", unit: "flat" },
  pathfinding: { label: "terrain penalty cut", unit: "flat" },
  wallIntegrity: { label: "wall integrity", unit: "flat" },
  guildSpells: { label: "spells a guild level", unit: "flat" },
  rangePenaltyCut: { label: "off the range penalty", unit: "bp" },
  necromancyPct: { label: "creatures raised", unit: "pct" },
  magicResist: { label: "magic resistance", unit: "pct" },
  eagleEye: { label: "spell learning", unit: "bp" },
  diplomacy: { label: "willingness to join", unit: "bp" },
  surrenderCut: { label: "off the surrender price", unit: "bp" },
  magicMirror: { label: "spells reflected", unit: "bp" },
});

/** False when a stat has no word here and the fallback would print its key. */
export function statHasWords(stat: string): boolean {
  return Object.prototype.hasOwnProperty.call(SKILL_STAT, stat);
}

/** One decimal, and only when there is one: 25 stays "25", 12.5 keeps its half. */
function trimNumber(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? groupDigits(rounded) : rounded.toFixed(1);
}

const withSign = (text: string, negative: boolean): string => (negative ? `-${text}` : `+${text}`);

/**
 * One modifier as a player reads it: "+25% damage", "+3 morale", "+250 gold a
 * day".
 *
 * `mulFp` is fixed point at `FP_ONE`, so what a player cares about is the
 * distance from 1: 1250 is a quarter more, 700 is three tenths less. Every
 * other op is the value in the stat's own unit.
 */
export function modifierPhrase(mod: Modifier): string {
  const words = SKILL_STAT[mod.stat] ?? { label: titleCase(mod.stat).toLowerCase(), unit: "flat" };
  if (mod.op === "mulFp") {
    const delta = (mod.value - FP_ONE) / 10;
    if (delta === 0) return `no change to ${words.label}`;
    return `${withSign(`${trimNumber(Math.abs(delta))}%`, delta < 0)} ${words.label}`;
  }
  const magnitude = words.unit === "bp" ? mod.value / 100 : mod.value;
  const shown = words.unit === "flat"
    ? trimNumber(Math.abs(magnitude))
    : `${trimNumber(Math.abs(magnitude))}%`;
  if (mod.op === "set") return `${words.label} ${trimNumber(magnitude)}`;
  // The kernel's names are the arithmetic, not the effect: `resolveStat`
  // applies "max" as `Math.max(v, value)`, which is a floor, and "min" as a
  // ceiling. Roadworn Boots carry `pathfinding max 3` and mean "never below
  // expert"; a cursed luck artifact carries `luck min` and means "never above".
  if (mod.op === "max") return `${words.label} never below ${trimNumber(magnitude)}`;
  if (mod.op === "min") return `${words.label} never above ${trimNumber(magnitude)}`;
  return `${withSign(shown, magnitude < 0)} ${words.label}`;
}

/**
 * What one tier of a skill is worth, in numbers, with duplicates collapsed.
 *
 * Exported for the test: the pair of `damageMin` / `damageMax` modifiers that
 * Offense and Archery carry has to read as one bonus, and a skill whose tiers
 * are all empty - Diplomacy changes a rule rather than a stat - has to come
 * back empty rather than as a blank chip.
 */
export function skillEffectPhrases(def: SecondarySkillDef, tier: number): readonly string[] {
  const mods = def.levels[tier - 1] ?? [];
  const out: string[] = [];
  for (const mod of mods) {
    const phrase = modifierPhrase(mod);
    if (!out.includes(phrase)) out.push(phrase);
  }
  return out;
}

/** True when no tier of the skill moves a number; see the panel's footer note. */
export function skillIsRuleOnly(def: SecondarySkillDef): boolean {
  return def.levels.every((tier) => tier.length === 0);
}

/* The speciality's computed bonus, as the tooltip prints it.
 *
 * The same functions the kernels read, at the hero's own level: a creature
 * specialty is the summon off `creatureBonusesFor` for the target's own def
 * ("+2 attack, +1 defense, +1-2 damage, +1 speed"), a number skill is the
 * specialty's amplification of the rung the hero holds ("+5% movement"), a
 * rule skill is the level the specialist gains ("counts as Advanced"), and a
 * spell is the damage share from `spellSpecialtyFp` ("+30% spell effect").
 * Empty when there is nothing to count yet - a level-1 tier-7 creature, or a
 * rule skill the hero has not learned - because a tooltip that prints "+0"
 * is promising a bonus the battle does not pay. */

export function specialtyCreatureBonus(
  content: ContentRegistry,
  heroDef: { readonly id: HeroDefId; readonly specialty: HeroDef["specialty"] },
  level: number,
): string {
  const target = heroDef.specialty.kind === "warMachine"
    ? `warmachine.${heroDef.specialty.target}`
    : heroDef.specialty.target;
  const bonuses = creatureBonusesFor(
    content,
    heroDef as unknown as Parameters<typeof creatureBonusesFor>[1],
    level,
  );
  const entry = bonuses.find((b) => (b.def as string) === target)
    ?? bonuses.find((b) => (b.def as string).endsWith(heroDef.specialty.target));
  const mods = entry?.mods ?? [];
  const attack = mods.filter((m) => m.stat === "attack").reduce((n, m) => n + m.value, 0);
  const defense = mods.filter((m) => m.stat === "defense").reduce((n, m) => n + m.value, 0);
  const dmgMin = mods.filter((m) => m.stat === "damageMin").reduce((n, m) => n + m.value, 0);
  const dmgMax = mods.filter((m) => m.stat === "damageMax").reduce((n, m) => n + m.value, 0);
  const speed = mods.filter((m) => m.stat === "speed").reduce((n, m) => n + m.value, 0);
  const parts: string[] = [];
  if (attack !== 0) parts.push(`+${String(attack)} attack`);
  if (defense !== 0) parts.push(`+${String(defense)} defense`);
  if (dmgMin !== 0 || dmgMax !== 0) {
    parts.push(dmgMin === dmgMax
      ? `+${String(dmgMin)} damage`
      : `+${String(dmgMin)}-${String(dmgMax)} damage`);
  }
  if (speed !== 0) parts.push(`+${String(speed)} speed`);
  return parts.join(", ");
}

export function specialtySkillBonus(
  content: ContentRegistry,
  heroDef: { readonly id: HeroDefId; readonly specialty: HeroDef["specialty"] },
  hero: HeroState,
  target: string,
): string {
  const skill = content.skills.get(target as Parameters<typeof content.skills.get>[0]);
  if (skill === undefined) return "";
  const held = skillLevel(hero, target as SkillDefId);
  if (held === 0) return "";
  const rung = skill.levels[held - 1] ?? [];
  if (rung.length === 0) {
    // A rule skill's bonus is a level, not a number: it reads through the same
    // `effectiveSkillLevel` the kernel's coded rules ask.
    const hd = heroDef as unknown as Parameters<typeof skillLevelBonus>[1];
    const bump = skillLevelBonus(content, hd, hero, target as Parameters<typeof skillLevelBonus>[3]);
    if (bump <= 0) return "";
    const effective = effectiveSkillLevel(
      content, hd, hero, target as Parameters<typeof effectiveSkillLevel>[3],
    );
    return `counts as ${MASTERY_NAME[effective] ?? "Expert"}`;
  }
  const pct = Math.min(SKILL_STEP_FP * hero.level, SKILL_CAP_FP) / 10;
  const words = rung.map((m) => modifierPhrase(m)).filter((p, i, all) => all.indexOf(p) === i);
  if (words.length === 0) return "";
  return `+${String(pct)}% of ${words.join(", ")}`;
}

export interface HeroScreenProps {
  readonly content: ContentRegistry;
  readonly hero: HeroState;
  /**
   * Resolved primaries from the kernel (`primaryFor`). The stored
   * `hero.primary` is the base; a Wyrmfang Crown is +4 Knowledge on top
   * of it and must show here, not there. Required, because the sheet used to
   * fall back to the base when the host left it out, and the fallback was
   * wrong for exactly the heroes it mattered on.
   */
  readonly primary: PrimarySkills;
  /**
   * Resolved mana cap from the kernel (`maxMana`). Required for the same
   * reason: the sheet used to fall back to knowledge x 10, and the kernel
   * also resolves a "mana" modifier, so the two disagreed whenever an
   * artifact or a skill touched the pool.
   */
  readonly manaMax: number;
  readonly army: ArmyState | null;
  /** A second army to trade with: a town garrison, or the hero standing beside. */
  readonly otherArmy?: ArmyState | null;
  readonly otherLabel?: string | undefined;
  /**
   * The hero on the other side of a meeting, when `otherArmy` is a hero's
   * rather than a garrison's. Present, the sheet grows the artifact half of
   * The classic's exchange window: every relic gains "Give to <name>", and the army
   * card gains the button that turns the sheet around so the traffic can run
   * the other way. Absent - a town garrison, or a hero standing alone - the
   * sheet is exactly what it was.
   */
  readonly otherHero?: HeroState | null;
  /** From the kernel, for `GIVE_ARTIFACT` out of the bag. */
  readonly giveReason?: ((backpackIndex: number) => Reason | null) | undefined;
  /**
   * Ask the kernel about any command, which is what the creature card's
   * "send across" button needs: a transfer's refusals are the kernel's
   * (`LAST_STACK`, `SLOT_OCCUPIED`, `NOT_ADJACENT`) and this screen can mirror
   * none of them honestly. Absent, the button is offered and a refusal is the
   * kernel's to make.
   */
  readonly checkCommand?: ((cmd: GameCommand) => Reason | null) | undefined;
  /** Swap which of the two meeting heroes the sheet belongs to. */
  readonly onSwapHeroes?: (() => void) | undefined;
  /** From the kernel. Falls back to the mirrored test in `heroModel`. */
  readonly equipReason?: (backpackIndex: number, slot: string) => Reason | null;
  readonly unequipReason?: (slot: string) => Reason | null;
  readonly onCommand: (cmd: GameCommand) => void;
  readonly onOpenSpellbook: () => void;
  readonly onClose: () => void;
  /**
   * The owner's colour: the classic wraps the hero sheet in the owning player's
   * velvet between the gilt mouldings and paints the paper doll's figure in
   * it. Absent or unknown, the band and the figure keep a neutral leather.
   */
  readonly ownerColour?: string | null;
}

interface ArtifactDrag {
  readonly source: "backpack" | "slot";
  readonly index: number;
  readonly slot: string;
  readonly def: ArtifactDef;
}

/** The velvet's three values plus the paper doll's fill: the owner's colour
 *  at 60%, so the figure reads as cloth under the sockets rather than paint. */
function heroVelvet(colour: string | null | undefined): CSSProperties {
  const vars = velvetVars(colour);
  if (Object.keys(vars).length === 0) return {};
  return { ...vars, "--hs-figure": "color-mix(in srgb, var(--hs-velvet-hi) 60%, transparent)" } as CSSProperties;
}

export function HeroScreen(props: HeroScreenProps): JSX.Element {
  usePolishStyles();
  useHeroStyles();
  const { content, hero } = props;
  const primary = props.primary;
  const manaMax = props.manaMax;
  const [rootRef, box] = useMeasure<HTMLDivElement>();
  const fit = usePhoneSheetFit();
  const phone = fit.phone;
  /* 0 while the observer has not reported yet; the roomy set is the right
     first paint because the screenshot harness settles before it shoots.
     Measured in DEVICE rows: a 768-tall window at 80% zoom is 960 CSS px
     and the same 768 rows of glass, and the roomy set does not fit them. */
  const dpr = useDevicePixelRatio();
  const dh = box.h * Math.min(1, dpr);
  const dw = box.w * Math.min(1, dpr);
  const compact = box.h > 0 && box.w > 0 && (dh < 880 || dw < 1500);
  /* The two IN HAND figures are the biggest reading on the sheet and the left
     column is the tightest: at 1366x768 the column asked for 748px of a 714px
     box, which pushed the Spell Points plaque 26px off the bottom of the window
     and took its caption with it. A bitmap face may only step by a WHOLE
     magnification, so it steps 4 -> 3 rather than being squeezed to fit. */
  const dialScale = useBitmapScale(4);
  /* The height the pack card has left for pockets, measured; see `packShelf`. */
  const [packRef, packBox] = useMeasure<HTMLDivElement>();
  /* The four primary plates share one row, so one measurement sizes them all. */
  const [primRef, primBox] = useMeasure<HTMLDivElement>();
  /**
   * The wording the four primary plates carry, chosen for the narrowest of
   * them - which is all of them, since the row is four equal columns.
   * `fitBitmapText` measures each candidate as it will actually be drawn (floor
   * applied, at the ratio the page is at), so this answers "does the word fit
   * on the glass", not "does it fit in the abstract".
   */
  const primLabels = useMemo(() => {
    const room = primBox.w > 0
      ? (primBox.w - 3 * PRIM_CELL_GAP_PX) / 4 - PRIM_CELL_CHROME_PX
      : 0;
    // Before the observer has reported, paint the full words: the roomy set is
    // the right first paint, and the screenshot harness settles before it shoots.
    if (room <= 0) return PRIMARY_SHORT;
    for (const p of PRIMARY_ORDER) {
      const full = PRIMARY_SHORT[p] ?? PRIMARY_LABEL[p] ?? "";
      const tight = PRIMARY_TIGHT[p] ?? full;
      if (fitBitmapText(room, [full, tight], 1, "serif", dpr, true) !== full) return PRIMARY_TIGHT;
    }
    return PRIMARY_SHORT;
  }, [primBox.w, dpr]);

  const heroDef = content.heroes.get(hero.def);
  /** The other hero of a meeting, by the name the player knows him by. */
  const otherName = props.otherHero == null
    ? ""
    : content.heroes.get(props.otherHero.def)?.name ?? titleCase(props.otherHero.def as string);
  /* What to call the two armies on a button. The hero's own side is always a
     hero; the far side is the other hero of a meeting, or whatever the host
     called it - a town's garrison. */
  const ownSideName = heroDef?.name ?? titleCase(hero.def as string);
  const otherSideName = props.otherHero == null
    ? (props.otherLabel ?? "the garrison")
    : otherName;
  const [drag, setDrag] = useState<ArtifactDrag | null>(null);
  const [dragArmy, setDragArmy] = useState<{ army: string; slot: number; single: boolean } | null>(null);
  const [target, setTarget] = useState<{ army: string; slot: number } | null>(null);
  /** A stack picked up from its card, waiting for the socket it goes to. See `Carry`. */
  const [carry, setCarry] = useState<Carry | null>(null);
  /** What the last tap while carrying had to say for itself. */
  const [carryNote, setCarryNote] = useState<string | null>(null);
  /** A split that knows both ends and is asking how many; the dialog issues the command. */
  const [splitAsk, setSplitAsk] = useState<{ from: Carry; to: { army: string; slot: number } } | null>(null);
  /**
   * The relic whose card is open, and where it is lying.
   *
   * Where it is lying is the half this used to throw away, and it is what makes
   * the card actionable: a relic in the pack can be put on and one on the doll
   * can be taken off, neither needing a drag. There is no HTML5 drag on a touch
   * screen at all, so on a phone this card was the only way to touch an
   * artifact and all it could do was describe one.
   */
  const [inspect, setInspect] = useState<
    { readonly def: ArtifactDef; readonly at: ArtifactWhere } | null
  >(null);
  /* The source socket travels with the card, because the card is now the
     click-to-move gesture's only handle: a touch screen fires no drag events,
     so without knowing WHICH socket was tapped there is nothing to send. */
  const [unit, setUnit] = useState<
    { def: CreatureDef; count: number; army: string; slot: number } | null
  >(null);
  const [skillInfo, setSkillInfo] = useState<
    { def: SecondarySkillDef; tier: number; picks: readonly SubskillDefId[] } | null>(null);
  const xp = useMemo(() => experienceBar(content, hero), [content, hero]);

  /* Morale and luck, resolved the way the battle resolves them. Depends on
     the ARMY as well as the hero: the morale rule is about how many
     alignments are marching together. */
  const fortune = useMemo(
    () => heroFortune(content, hero, props.army ?? null), [content, hero, props.army]);

  /* The speciality, resolved to whatever it points at. It is three lines of
     lookup and it is the difference between a hero who is a bag of numbers and
     a hero who is Jorthas, who makes Bless worth casting. The `bonus` is the
     same computed number the kernels apply at this hero's level - creature
     attack/damage shares, the skill amplification, the spell percentage - so
     the sheet and the battle never disagree. */
  const special = useMemo(() => {
    const sp = heroDef?.specialty;
    if (sp === undefined) return { icon: null, name: "None", kind: "", bonus: "" };
    if (sp.kind === "spell") {
      const def = content.spells.get(sp.target as SpellDefId);
      const fp = heroDef === undefined ? {} : spellSpecialtyFp(heroDef, hero.level);
      const gain = fp[sp.target] ?? FP_ONE;
      const pct = Math.round(((gain - FP_ONE) * 100) / FP_ONE);
      return {
        icon: def === undefined ? null : spellIcon(def),
        name: def?.name ?? titleCase(sp.target),
        kind: "Spell",
        bonus: pct > 0 ? `+${String(pct)}% spell effect` : "",
      };
    }
    if (sp.kind === "creature" || sp.kind === "warMachine") {
      const key = sp.kind === "creature"
        ? sp.target as Parameters<typeof content.creatures.get>[0]
        : `warmachine.${sp.target}` as Parameters<typeof content.creatures.get>[0];
      const def = content.creatures.get(key);
      const bonus = heroDef === undefined ? "" : specialtyCreatureBonus(content, heroDef, hero.level);
      return {
        icon: def === undefined ? null : creatureIcon(def),
        name: sp.kind === "creature"
          ? (def?.namePlural ?? titleCase(sp.target))
          : (def?.name ?? titleCase(sp.target)),
        kind: sp.kind === "creature" ? "Creature" : "WarMachine",
        bonus,
      };
    }
    if (sp.kind === "resource") {
      // A resource specialty IS its number - "+350 gold a day" - and the
      // target names no skill, so the skill branch below printed "Gold".
      const phrases = sp.modifiers.map(modifierPhrase);
      return {
        icon: null,
        name: phrases.join(", ") || titleCase(sp.target),
        kind: "Resource",
        bonus: phrases.join(", "),
      };
    }
    const skill = content.skills.get(sp.target as Parameters<typeof content.skills.get>[0]);
    const bonus = heroDef === undefined ? ""
      : specialtySkillBonus(content, heroDef, hero, sp.target);
    return {
      icon: skill === undefined ? null : uiIconKey(skill.art),
      name: skill?.name ?? titleCase(sp.target),
      kind: titleCase(sp.kind),
      bonus,
    };
  }, [content, heroDef, hero]);

  /* What the CLASS is worth, as words. The table is shared by every hero of
     the class, so it is a line under the speciality rather than a speciality
     of its own; empty for a content bundle that ships no table. */
  const classPassive = useMemo(() => {
    if (heroDef === undefined) return "";
    return (content.levels.classPassive?.[heroDef.heroClass] ?? []).map(modifierPhrase).join(", ");
  }, [content, heroDef]);

  /* The sheet's tooltip for the speciality cell: name, the computed bonus at
     this hero's level, and the class passive behind it. Declared after the
     class line it reads, so the order on the page is the order in the file. */
  const specialTitle = useMemo(() => {
    const base = `Speciality: ${special.name}`;
    const withBonus = special.bonus === "" ? base : `${base} (${special.bonus})`;
    return classPassive === ""
      ? withBonus
      : `${withBonus}. ${heroDef?.heroClass ?? "Class"}: ${classPassive}`;
  }, [special, classPassive, heroDef]);

  /* The pack is drawn as a full shelf of pockets rather than as however many
     artifacts happen to be in it: five items in a card sized for twenty leaves a
     hole, and an empty socket is the thing you drag *onto*.

     The shelf is **measured**, and that is the fix for the card that reserved
     space it never filled. The pack card is `flex: 1 1 auto` in the gear column
     - it is handed whatever height the doll plate above it does not use - so a
     constant row count is a guess at a number the layout decides, and the guess
     was 1 where the room was 4: measured off the shipped 1366x768 shot, a
     five-artifact pack drew one 44px row in a 256px card and left 183px of bare
     wood under it, with no empty pocket on the shelf at all - three lines under
     a comment calling an empty socket the thing you drag onto.

     Filling it is the right half of the choice rather than shrinking the card,
     because the height is not this card's to give back: the doll plate above it
     is `flex: 0 0 auto` precisely so the slack lands here, and a shorter card
     would move the same bare strip out onto the panel behind it. The kernel
     puts no capacity on `hero.backpack` either - The classic's own pack is an
     unbounded scrolling strip - so how many pockets to draw is a presentation
     choice and not a rule, and the honest one is "as many as this card holds".

     Whole rows of full-size pockets leave a remainder the shelf cannot use -
     measured on the well itself, 73px of 223px at 1920x1080, four pixels short
     of a third 72px row, and 33px of 221px at 1366x768. So the last row is
     bought by taking those few pixels off *every* pocket rather than by leaving
     the strip bare: a pocket may shrink to `PACK_CELL_MIN` to claim a row, and
     never below it, because a socket smaller than that stops reading as a
     socket. The pocket stays square and the icon keeps its inset, so what
     changes is the side of a recess and nothing else. Measured after: 3 rows of
     70px in 222 of 223px, and 5 rows of 41px in 221 of 221px. */
  const packShelf = useMemo((): { readonly rows: number; readonly cell: number } => {
    const max = compact ? PACK_CELL_COMPACT : PACK_CELL;
    const min = compact ? PACK_CELL_MIN_COMPACT : PACK_CELL_MIN;
    const gap = compact ? PACK_GAP_COMPACT : PACK_GAP;
    const h = packBox.h;
    // Never fewer rows than the pack holds, and never a shelf with nowhere to
    // drop: the extra cell is the empty pocket the drag handler needs.
    const needed = Math.ceil((hero.backpack.length + 1) / PACK_COLS);
    /**
     * A phone takes every row at the FULL pocket, and lets the sheet grow.
     *
     * Dividing the card's leftover height is a desktop answer: on a 390px
     * phone the leftover was 38px, so the pack became an 18px-tall scroller
     * with half a row of pockets in it - the relics were in there, and no
     * thumb could ever reach them. The sheet's own body scrolls now
     * (`hs-hero--phone .hs-screen__body`), so the strip has somewhere to grow
     * into and a nested scroller inside a scaled plate can go.
     *
     * `PACK_CELL` and not the compact one, even though a phone is compact by
     * every other measure: the plate is drawn at about half size there, so the
     * 44px compact pocket lands on the glass as 21px and a relic cannot be
     * picked up with a finger. The full 72 lands at 34, which is a target.
     */
    // One row on a phone: the pager below the strip walks the rest.
    if (phone.phone) return { rows: 1, cell: PACK_CELL };
    // Before the observer has reported there is nothing to divide.
    if (h <= 0) return { rows: Math.max(1, needed), cell: max };
    // n rows occupy n * cell + (n - 1) * gap, so the cell an n-row shelf implies
    // is the height less its gaps, shared out.
    const cellFor = (n: number): number => Math.floor((h - (n - 1) * gap) / n);
    let rows = Math.max(1, Math.floor((h + gap) / (max + gap)));
    while (cellFor(rows + 1) >= min) rows += 1;
    // Only the rows that fit: a pack that outruns them is paged, below, the
    // way the phone's is. It used to grow the strip into a nested scroller,
    // which on a full plate is a bar nobody sees and a sixteenth relic nobody
    // finds.
    return { rows, cell: Math.max(min, Math.min(max, cellFor(rows))) };
  }, [compact, phone.phone, packBox.h, hero.backpack.length]);
  const packRows = packShelf.rows;

  /**
   * The pack, a page at a time.
   *
   * A phone shows ONE row of five pockets and steps through the rest, because
   * the alternative on that screen is a strip that runs off the bottom of a
   * plate the player has to know to scroll. The desktop shows the rows its
   * shelf has height for and steps through the rest for the same reason: it
   * used to scroll inside the strip, and a scrollbar on a scored plate is the
   * least visible thing on the sheet. Paging is a pair of arrows and a count
   * - it says how many relics there are, which scrolling never did - and it
   * puts the next relic one click away instead of one gesture nobody made.
   * The wheel over the strip turns the page too, so the scroll a player
   * reaches for still works.
   *
   * Everything downstream works in ABSOLUTE indices: `EQUIP_ARTIFACT` names a
   * `backpackIndex` into the hero's own pack, and a page offset that leaked
   * into that would equip the wrong relic on every page but the first.
   */
  const packPageSize = phone.phone ? PACK_COLS : packRows * PACK_COLS;
  const packPages = Math.max(1, Math.ceil(hero.backpack.length / packPageSize));
  const [packPage, setPackPage] = useState(0);
  // A pack that shrank under the page - the last relic on it was equipped -
  // must not leave the player looking at an empty shelf with no way back.
  const page = Math.min(packPage, packPages - 1);
  useEffect(() => {
    if (packPage !== page) setPackPage(page);
  }, [packPage, page]);
  const packFrom = page * packPageSize;

  const backpackCells = useMemo((): readonly (ArtifactDefId | null)[] => {
    const cells: (ArtifactDefId | null)[] = hero.backpack
      .slice(packFrom, packFrom + packPageSize);
    while (cells.length < packPageSize) cells.push(null);
    return cells;
  }, [hero.backpack, packFrom, packPageSize]);

  /**
   * A wheel notch is one page, not one per event: a wheel fires a run of
   * events for a single flick, so a turn is followed by a short deaf spell.
   */
  const lastWheelTurn = useRef(0);
  const wheelPack = useCallback((e: { readonly deltaY: number; readonly timeStamp: number }): void => {
    if (packPages <= 1 || e.deltaY === 0) return;
    if (e.timeStamp - lastWheelTurn.current < PACK_WHEEL_DEAF_MS) return;
    const next = e.deltaY > 0 ? Math.min(packPages - 1, page + 1) : Math.max(0, page - 1);
    if (next === page) return;
    lastWheelTurn.current = e.timeStamp;
    setPackPage(next);
  }, [packPages, page]);

  const equipReason = useCallback((index: number, slot: string): Reason | null =>
    props.equipReason?.(index, slot) ?? equipIllegality(content, hero, index, slot),
  [props, content, hero]);

  const unequipReason = useCallback((slot: string): Reason | null =>
    props.unequipReason?.(slot) ?? unequipIllegality(hero, slot),
  [props, hero]);

  /* No mirrored fallback: whether a hero may hand a relic to the one beside
     him is the kernel's adjacency and the kernel's turn, neither of which this
     screen can see. With no host answer the button is simply offered and the
     kernel refuses it, which is the same bargain `checkCommand` makes
     everywhere else on this sheet. */
  const giveReason = useCallback((index: number): Reason | null =>
    props.giveReason?.(index) ?? null,
  [props]);

  const armies = useMemo(() => {
    const m = new Map<string, ArmyState>();
    if (props.army !== null && props.army !== undefined) m.set(props.army.id as string, props.army);
    const other = props.otherArmy ?? null;
    if (other !== null) m.set(other.id as string, other);
    return m;
  }, [props.army, props.otherArmy]);

  const dropArmy = useCallback((armyId: string, slot: number) => {
    const d = dragArmy;
    setDragArmy(null);
    setTarget(null);
    if (d === null) return;
    const from = armies.get(d.army);
    const to = armies.get(armyId);
    if (from === undefined || to === undefined) return;
    const cmd = armyDropCommand({ fromArmy: from, fromSlot: d.slot, toArmy: to, toSlot: slot, single: d.single });
    if (cmd !== null) props.onCommand(cmd);
  }, [dragArmy, armies, props]);

  const dropCarry = useCallback(() => { setCarry(null); setCarryNote(null); setSplitAsk(null); }, []);

  /**
   * A tap on an army socket. With nothing in hand it opens the creature's card;
   * with a stack in hand it is the drop, gated by the same kernel verdict a
   * drag's drop is. An illegal drop keeps the stack in hand and says why, so a
   * thumb that missed is not a thumb that has to start over.
   */
  const tapSocket = useCallback((armyId: string, slot: number, def: CreatureDef | null, count: number) => {
    if (carry === null) {
      if (def !== null) setUnit({ def, count, army: armyId, slot });
      return;
    }
    const outcome = carryTap(carry, { army: armyId, slot }, armies);
    if (outcome.kind === "ask") { setSplitAsk({ from: outcome.from, to: outcome.to }); return; }
    if (outcome.kind === "noop") { setCarryNote(outcome.why); return; }
    const reason = props.checkCommand?.(outcome.cmd) ?? null;
    if (reason !== null) { setCarryNote(explainReason(content, reason)); return; }
    props.onCommand(outcome.cmd);
    dropCarry();
  }, [carry, armies, props, content, dropCarry]);

  /**
   * What the combination shelf offers: sets this hero can put together, and
   * sets he is wearing and could take apart.
   *
   * Both questions are the kernel's own - `assemblableFor` and
   * `disassembleIllegality` are the same functions `legal.ts` enumerates
   * through - so a button that appears is a command the kernel accepts, and
   * this screen never has to know what a component is.
   */
  const combos = useMemo((): readonly {
    readonly kind: "make" | "break";
    readonly def: ArtifactDef;
    readonly command: GameCommand;
  }[] => {
    const out: { kind: "make" | "break"; def: ArtifactDef; command: GameCommand }[] = [];
    for (const id of assemblableFor(content, hero)) {
      const def = content.artifacts.get(id);
      if (def === undefined) continue;
      out.push({
        kind: "make", def,
        command: { type: "ASSEMBLE_ARTIFACT", hero: hero.id, artifact: id },
      });
    }
    for (const slot of Object.keys(hero.artifacts).sort()) {
      if (disassembleIllegality(content, hero, slot) !== null) continue;
      const id = hero.artifacts[slot];
      const def = id == null ? undefined : content.artifacts.get(id);
      if (def === undefined) continue;
      out.push({
        kind: "break", def,
        command: { type: "DISASSEMBLE_ARTIFACT", hero: hero.id, slot },
      });
    }
    return out;
  }, [content, hero]);

  const dropOnSlot = useCallback((slot: string) => {
    const d = drag;
    setDrag(null);
    if (d === null || d.source !== "backpack") return;
    if (equipReason(d.index, slot) !== null) return;
    props.onCommand({ type: "EQUIP_ARTIFACT", hero: hero.id, backpackIndex: d.index, slot });
  }, [drag, equipReason, props, hero.id]);

  const dropOnBackpack = useCallback(() => {
    const d = drag;
    setDrag(null);
    if (d === null || d.source !== "slot") return;
    if (unequipReason(d.slot) !== null) return;
    props.onCommand({ type: "UNEQUIP_ARTIFACT", hero: hero.id, slot: d.slot });
  }, [drag, unequipReason, props, hero.id]);

  /** One doll socket, placed on the lattice by `DOLL`'s own column and row. */
  const dollSlot = (
    cell: { readonly slot: string; readonly label: string; readonly col: number; readonly row: number },
    size: number,
  ): JSX.Element => {
    const place: CSSProperties = { gridColumn: cell.col, gridRow: cell.row };
    const id = hero.artifacts[cell.slot] ?? null;
    const def = id === null ? null : content.artifacts.get(id) ?? null;
    const reason = drag === null || drag.source !== "backpack"
      ? null
      : equipReason(drag.index, cell.slot);
    const active = drag !== null && drag.source === "backpack";
    const cls = [
      "hs-slot",
      id === null ? "hs-slot--empty" : "",
      active && reason === null ? "hs-slot--drop" : "",
      active && reason !== null ? "hs-slot--illegal" : "",
    ].filter((c) => c.length > 0).join(" ");
    return (
      <button
        key={cell.slot}
        type="button"
        className={cls}
        style={place}
        title={active && reason !== null ? explainReason(content, reason) : def?.name ?? cell.label}
        aria-label={`${cell.label}: ${def?.name ?? "empty"}`}
        draggable={def !== null}
        onDragStart={() => {
          if (def !== null) setDrag({ source: "slot", index: -1, slot: cell.slot, def });
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); dropOnSlot(cell.slot); }}
        onClick={() => { if (def !== null) setInspect({ def, at: { kind: "slot", slot: cell.slot } }); }}
      >
        {/* An empty socket carries NO lettering. "MISC MISC MISC MISC BOOK"
            in letterspaced small caps was seven ghost words on the plate and
            at 1366x768 they were the most legible thing on it. The comment
            that defended them was right about the problem - nineteen identical
            holes read as an unfinished grid - and wrong about the remedy.
            the classic solved it with the figure, which is now drawn behind these
            and shows through every empty one. */}
        {def === null ? null : (
          <MaybeIcon
            atlasKey={artifactIcon(def)}
            size={size}
            className="hs-slot__art"
            title={def.name}
          />
        )}
      </button>
    );
  };

  return (
    <div
      className={`hs-screen hs-screen--sheet hm-hs__screen ${compact ? "hs-hero--compact" : ""}${phone.phone ? " hs-hero--phone" : ""}${props.otherHero == null ? "" : " hs-hero--meeting"}`}
      style={{ ...heroVelvet(props.ownerColour), ...fit.style } as CSSProperties}
      ref={(el) => { attachRefs(el, rootRef, fit.screenRef); }}
      role="region"
      aria-label={`${heroDef?.name ?? "Hero"} sheet`}
    >
      <ScreenStyles />

      {/* No bar across the top. The classic's hero sheet is a dialog over the
          adventure map - a carved frame with the sheet inside it and the two
          buttons on the sheet's own foot - and a screen-wide plank with the
          same two buttons at its far right was the loudest thing on ours that
          the reference does not have. The sheet floats on the overlay's scrim
          (`.hs-screen--sheet`), which is what lets the map show through. */}
      <div className="hs-screen__body">
        <div className="hs-hero" ref={fit.sheetRef}>
          {/* ------- the hero himself, in the reference's own order.
              the classic's left panel reads top to bottom: face and name,
              the four primaries in a row, specialty beside morale and luck,
              experience beside spell points, then the eight secondary skills
              two to a row. Every reading sits in a thin gilt cell on one
              leather field - no group boxes, no headings. */}
          <div className="hs-hero__col">
            <div className="hs-card hs-wood hm-hs__sheet">
              <div className="hm-hs__head">
                <div className="hs-portrait hm-hs__face">
                  <MaybeIcon
                    atlasKey={heroDef === undefined ? null : heroPortrait(heroDef.portrait)}
                    size={compact ? PORTRAIT_COMPACT / 2 : PORTRAIT_COMPACT}
                    title={heroDef?.name ?? "Hero"}
                  />
                </div>
                <div className="hm-hs__title">
                  {/* Stamped, both lines: the name in the display face at
                      magnification 2 (cap 22, the classic's bigfont) and the class
                      line in the text face. They were the last two typeset
                      anti-aliased strings on a panel whose captions are all
                      bitmap, and the mixture read as two kits on one plate. */}
                  <div className="hs-hero__name">
                    <BitmapText face="serifDisplay" shadow scale={compact ? 1 : 2}>
                      {heroDef?.name ?? titleCase(hero.def as string)}
                    </BitmapText>
                  </div>
                  <div className="hs-hero__class">
                    <BitmapText face="serif" shadow scale={1}>
                      {`Level ${String(hero.level)} ${heroDef?.heroClass ?? "Wanderer"}`}
                    </BitmapText>
                  </div>
                  {/* Two facts, two marks, a word of air between them - not
                      one string joined by a middot. The classic never joins two
                      fields with a separator character; a reviewer comparing
                      our hero sheet against the game's named the middot as the
                      thing that made ours read as a dashboard. */}
                  <div className="hm-hs__move" title={`Movement left: ${groupDigits(hero.movementLeft)}`}>
                    <BitmapText face="serif" shadow scale={1}>
                      {titleCase(hero.faction as string)}
                    </BitmapText>
                    <BitmapText face="serif" shadow scale={1}>
                      {`Movement ${groupDigits(hero.movementLeft)}`}
                    </BitmapText>
                  </div>
                </div>
              </div>

              {/* Four medallions with the word above and the figure below,
                  which is the reference's own row; the word is short because
                  the reference's is - "Power", not "Spell Power". */}
              <div className="hs-primaries" ref={primRef}>
                {PRIMARY_ORDER.map((p) => (
                  <div className="hs-prim" key={p} title={`${PRIMARY_LABEL[p]}: ${String(primary[p])}`}>
                    {/* The full word is always in the plate's own tooltip, so a
                        narrow window shortens what is stamped and never what
                        the player can find out. */}
                    <span className="hs-prim__label">
                      <BitmapText face="serif" shadow scale={1}>{primLabels[p] ?? PRIMARY_LABEL[p]}</BitmapText>
                    </span>
                    <span className="hs-prim__socket">
                      <MaybeIcon atlasKey={PRIMARY_ICON_BY_KEY[p] ?? null}
                        size={compact ? PRIM_ICON_COMPACT : PRIM_ICON} title={PRIMARY_LABEL[p]}
                        className="hm-relief__inlay" />
                    </span>
                    <span className="hs-prim__value">
                      {/* Serif, as the reference stamps its four figures. The compact
                          plate was cut for a 14px numeral, so it takes the display face
                          at 1 (cap 12) rather than the text face at 2 (cap 18). */}
                      {compact
                        ? <BitmapText face="serifDisplay" shadow scale={1}>{primary[p]}</BitmapText>
                        : <BitmapText face="serif" shadow scale={2}>{primary[p]}</BitmapText>}
                    </span>
                  </div>
                ))}
              </div>

              {/* Specialty, morale, luck - one row, three cells. See
                  `heroFortune`: the army term is the kernel's own. */}
              <div className="hm-hs__fortune">
                <div className="hm-hs__cell hs-special"
                  title={specialTitle}>
                  <span className="hs-special__socket">
                    <MaybeIcon atlasKey={special.icon} size={compact ? ART_ICON : ART_ICON_LARGE}
                      title={special.name} className="hm-relief__inlay" />
                  </span>
                  <span className="hm-hs__celltext">
                    <span className="hm-hs__cellcap">
                      <BitmapText face="serif" shadow scale={1}>Specialty</BitmapText>
                    </span>
                    {/* Stamped like `Specialty` over it: it was the one typeset run on
                        the sheet, 40 grey levels beside 1-6 for every neighbour. */}
                    <span className="hs-special__name">
                      <BitmapText face="serif" shadow scale={1}>{special.name}</BitmapText>
                    </span>
                  </span>
                </div>
                {([["morale", fortune.morale], ["luck", fortune.luck]] as const).map(([kind, f]) => (
                  <div className="hm-hs__cell hm-hs__mood" key={kind}
                    title={`${titleCase(kind)} ${f.value >= 0 ? "+" : ""}${String(f.value)} — ${fortuneWord(f.value)}${
                      f.terms.length === 0 ? "" : `. ${f.terms.map(
                        (t) => `${t.why} ${t.value >= 0 ? "+" : ""}${String(t.value)}`).join(", ")}`}`}>
                    <span className="hs-special__socket">
                      <MaybeIcon atlasKey={MOOD_ICON(kind, f.value)}
                        size={compact ? ART_ICON : MOOD_ICON_SIZE} title={titleCase(kind)}
                        className="hm-relief__inlay" />
                    </span>
                    <span className="hm-hs__moodnum">
                      <BitmapText scale={2}>
                        {`${f.value > 0 ? "+" : ""}${String(f.value)}`}
                      </BitmapText>
                    </span>
                  </div>
                ))}
              </div>

              {/* Experience beside spell points, each an icon and two lines,
                  exactly as the reference lays them. The bar under the
                  experience figure is ours; it costs four rows and answers
                  the one question the raw figure does not. */}
              <div className="hm-hs__ledger">
                <div className="hm-hs__cell hm-hs__dial"
                  title={xp.maxed ? "Maximum level" : `${groupDigits(xp.toNext)} to level ${String(hero.level + 1)}`}>
                  <span className="hs-special__socket">
                    <MaybeIcon atlasKey={DIAL_ICON_KEY["experience"] ?? null}
                      size={compact ? ART_ICON : DIAL_ICON} title="Experience"
                      className="hm-relief__inlay" />
                  </span>
                  <span className="hm-hs__celltext">
                    <span className="hm-hs__cellcap">
                      <BitmapText face="serif" shadow scale={1}>Experience</BitmapText>
                    </span>
                    <span className="hm-hs__dialnum">
                      <BitmapText face={dialScale >= 4 ? "serifDisplay" : "serif"} shadow scale={2}>
                        {groupDigits(xp.current)}
                      </BitmapText>
                    </span>
                    <span className="hs-bar hm-hs__xpbar" role="img"
                      aria-label={`Experience ${groupDigits(xp.current)}`}>
                      <span className="hs-bar__fill" style={{ width: `${String(xp.progressBp / 100)}%` }} />
                    </span>
                  </span>
                </div>
                <div className="hm-hs__cell hm-hs__dial" title={`Spell points: ${groupDigits(hero.mana)}`}>
                  <span className="hs-special__socket">
                    <MaybeIcon atlasKey={DIAL_ICON_KEY["mana"] ?? null}
                      size={compact ? ART_ICON : DIAL_ICON} title="Spell points"
                      className="hm-relief__inlay" />
                  </span>
                  <span className="hm-hs__celltext">
                    <span className="hm-hs__cellcap">
                      <BitmapText face="serif" shadow scale={1}>Spell Points</BitmapText>
                    </span>
                    <span className="hm-hs__dialnum">
                      <BitmapText face={dialScale >= 4 ? "serifDisplay" : "serif"} shadow scale={2}>
                        {`${groupDigits(hero.mana)}/${groupDigits(manaMax)}`}
                      </BitmapText>
                    </span>
                  </span>
                </div>
              </div>

              {/* Eight skills, two to a row: a medallion and, beside it, the
                  rank over the name. The rules text is one click away. */}
              {hero.secondary.length === 0 ? (
                <div className="hs-tiny hs-muted">No secondary skills yet.</div>
              ) : (
                <div className="hs-skills">
                  {hero.secondary.map((s: SecondarySkillState) => (
                    <SkillPlate
                      key={s.def as string}
                      def={content.skills.get(s.def) ?? null}
                      skill={s}
                      compact={compact}
                      onOpen={setSkillInfo}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ------- the equipment plate: ONE object, the doll with the pack
              as a strip across its foot. `DOLL` in `heroModel.ts` declares the
              reference's own irregular arrangement - head at the crown, hands
              at the ends of the arms, rings at hip height, boots at the hem,
              war machines below the body. */}
          <div className="hs-hero__main">
            <div
              className="hs-card hs-wood hs-hero__gear"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); dropOnBackpack(); }}
            >
              <div className="hm-hs__plate">
                <div className="hm-hs__figure">
                  <svg className="hm-hs__body" viewBox="0 0 100 121"
                    preserveAspectRatio="none" aria-hidden="true" focusable="false">
                    <polygon points={FIGURE_POINTS} />
                  </svg>
                  {DOLL.map((cell) => dollSlot(cell, compact ? DOLL_CELL_COMPACT : DOLL_CELL))}
                </div>
                <div className="hm-hs__packrule" aria-hidden="true" />
                <div className="hm-hs__packwell">
                  {/* Combination artifacts are assembled and taken apart from
                      the artifact's own info dialog (click a slot), which is
                      where the classic keeps it. The row of "Take apart" chips that
                      stood here clipped through its own letters at 1366x768. */}
                  <div className={`hm-hs__packgrid${packPages > 1 ? " hm-hs__packgrid--paged" : ""}`}
                    ref={packRef} onWheel={wheelPack}>
                  <div
                    className="hs-backpack"
                    style={{ gridTemplateColumns: `repeat(${String(PACK_COLS)}, minmax(0, ${String(packShelf.cell)}px))` }}
                  >
                    {backpackCells.map((id: ArtifactDefId | null, i) => {
                      if (id === null) {
                        return <span className="hs-slot hs-slot--empty" key={`empty:${String(i)}`}
                          style={{ maxWidth: `${String(packShelf.cell)}px` }} aria-hidden="true" />;
                      }
                      return ((): JSX.Element => {
                        const def = content.artifacts.get(id) ?? null;
                        return (
                          <button
                            key={`${id as string}:${String(i)}`}
                            type="button"
                            className={`hs-slot ${drag?.source === "backpack" && drag.index === packFrom + i ? "hs-slot--dragging" : ""}`}
                            style={{ maxWidth: `${String(packShelf.cell)}px` }}
                            title={def?.name ?? titleCase(id as string)}
                            aria-label={def?.name ?? titleCase(id as string)}
                            draggable={def !== null}
                            onDragStart={() => {
                              if (def !== null) {
                                setDrag({ source: "backpack", index: packFrom + i, slot: def.slot, def });
                              }
                            }}
                            onClick={() => {
                              if (def !== null) {
                                setInspect({ def, at: { kind: "backpack", index: packFrom + i } });
                              }
                            }}
                          >
                            {def === null ? null : (
                              <MaybeIcon atlasKey={artifactIcon(def)}
                                size={packShelf.cell} className="hs-slot__art" title={def.name} />
                            )}
                          </button>
                        );
                      })();
                    })}
                  </div>
                  </div>
                  {/* The pager. Only when there is a page to go to: one shelf
                      is the whole pack for most heroes and a dead arrow under
                      it would be furniture. */}
                  {packPages <= 1 ? null : (
                    <div className="hm-hs__packpager">
                      <button
                        type="button"
                        className="hs-plate hm-hs__packstep"
                        disabled={page === 0}
                        aria-label="Earlier relics"
                        onClick={() => { setPackPage(Math.max(0, page - 1)); }}
                      >
                        ▲
                      </button>
                      <span className="hm-hs__packcount" aria-live="polite">
                        {phone.phone ? (
                          <BitmapText face="serif" shadow scale={1}>
                            {`${String(packFrom + 1)}-${String(Math.min(hero.backpack.length, packFrom + packPageSize))} of ${String(hero.backpack.length)}`}
                          </BitmapText>
                        ) : (
                          <>
                            <BitmapText face="serif" shadow scale={1}>
                              {`${String(packFrom + 1)}-${String(Math.min(hero.backpack.length, packFrom + packPageSize))}`}
                            </BitmapText>
                            <BitmapText face="serif" shadow scale={1}>
                              {`of ${String(hero.backpack.length)}`}
                            </BitmapText>
                          </>
                        )}
                      </span>
                      <button
                        type="button"
                        className="hs-plate hm-hs__packstep"
                        disabled={page >= packPages - 1}
                        aria-label="More relics"
                        onClick={() => { setPackPage(Math.min(packPages - 1, page + 1)); }}
                      >
                        ▼
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* The army band across the foot, with the sheet's two buttons at
              its right end - where the reference keeps its own. */}
          <div className="hs-card hs-wood hs-hero__armycard">
            {carry === null ? null : (() => {
                const src = armies.get(carry.army)?.slots[carry.slot] ?? null;
                const def = src === null ? null : content.creatures.get(src.def) ?? null;
                const what = src === null || def === null ? "the stack"
                  : carry.count === null ? `${groupDigits(src.count)} ${src.count === 1 ? def.name : def.namePlural}`
                  : carry.count === "ask" ? `some of the ${groupDigits(src.count)} ${def.namePlural}`
                  : `${groupDigits(carry.count)} ${carry.count === 1 ? def.name : def.namePlural}`;
              return (
                <div className="hm-hs__carryhint" role="status">
                  <span className="hs-tiny">{carryNote ?? `Tap a socket for ${what}.`}</span>
                  <button type="button" className="hs-btn" onClick={dropCarry}>Cancel</button>
                </div>
              );
            })()}
            <div className="hs-hero__muster">
              <HeroArmyRow
                content={content}
                army={props.army}
                label={props.otherHero == null ? "Marching with the hero"
                  : `${heroDef?.name ?? titleCase(hero.def as string)}'s army`}
                ground={phone.phone ? 64 : compact ? ARMY_GROUND_COMPACT : ARMY_GROUND}
                plinth={compact ? ARMY_PLINTH_COMPACT : ARMY_PLINTH}
                countScale={compact ? ARMY_COUNT_SCALE_COMPACT : ARMY_COUNT_SCALE}
                dragSource={dragArmy}
                dropTarget={target}
                carry={carry}
                onDragStart={(a, s, single) => setDragArmy({ army: a, slot: s, single })}
                onDragEnter={(a, s) => setTarget({ army: a, slot: s })}
                onDrop={dropArmy}
                onTap={tapSocket}
              />
              {props.otherArmy == null ? null : (
                <HeroArmyRow
                  content={content}
                  army={props.otherArmy}
                  label={props.otherLabel ?? "Garrison"}
                  ground={phone.phone ? 64 : compact ? ARMY_GROUND_COMPACT : ARMY_GROUND}
                  plinth={compact ? ARMY_PLINTH_COMPACT : ARMY_PLINTH}
                  countScale={compact ? ARMY_COUNT_SCALE_COMPACT : ARMY_COUNT_SCALE}
                  dragSource={dragArmy}
                  dropTarget={target}
                  carry={carry}
                  onDragStart={(a, s, single) => setDragArmy({ army: a, slot: s, single })}
                  onDragEnter={(a, s) => setTarget({ army: a, slot: s })}
                  onDrop={dropArmy}
                  onTap={tapSocket}
                />
              )}
              <div className="hm-hs__buttons">
                {/* Turn the meeting around. Units already flow both ways -
                    both army rows are drag sources and drop targets - but the
                    paper doll, the bag and the spellbook are one hero's, so
                    without this the second hero could receive and never give. */}
                {props.otherHero == null || props.onSwapHeroes === undefined ? null : (
                  <button type="button" className="hs-plate hm-hs__button"
                    onClick={props.onSwapHeroes}
                    title={`Show ${otherName}'s side of the meeting`}>
                    <BitmapText face={compact ? "serif" : "serifDisplay"} shadow scale={1}>SWITCH</BitmapText>
                  </button>
                )}
                <button type="button" className="hs-plate hm-hs__button" onClick={props.onOpenSpellbook}
                  disabled={hero.spellbook.length === 0}
                  title={hero.spellbook.length === 0 ? "This hero has no spellbook." : "Open the spellbook"}>
                  <BitmapText face={compact ? "serif" : "serifDisplay"} shadow scale={1}>SPELLBOOK</BitmapText>
                </button>
                <button type="button" className="hs-plate hm-hs__button hm-hs__button--close"
                  onClick={props.onClose} title="Close the hero sheet">
                  <BitmapText face={compact ? "serif" : "serifDisplay"} shadow scale={1}>CLOSE</BitmapText>
                </button>
              </div>
            </div>
            {/* No drag-hint sentence under the band: the reference prints
                nothing there, and a typeset instruction line under a row of
                painted sockets was one of the tells. The rule lives in the
                socket tooltips. */}
          </div>
        </div>
      </div>

      {inspect === null ? null : (
        <ScreenDialog title={inspect.def.name} onClose={() => setInspect(null)}
          width={phone.phone ? 340 : 480}>
          <div style={{ display: "flex", gap: phone.phone ? "8px" : "12px" }}>
            <div className="hs-portrait">
              <MaybeIcon atlasKey={artifactIcon(inspect.def)}
                size={phone.phone ? ART_ICON_LARGE : ART_ICON_LARGE * 2}
                title={inspect.def.name} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="hs-tiny hs-muted" style={{ marginBottom: "6px" }}>
                {`${titleCase(inspect.def.rank)} · ${titleCase(inspect.def.slot)} · ${groupDigits(inspect.def.cost)} gold`}
              </div>
              <div className="hs-tiny" style={{ lineHeight: 1.45 }}>{inspect.def.description}</div>
            </div>
          </div>
          {/* Put it on, or take it off, without a drag.
              the classic equips by dragging and so does this sheet on a mouse - but
              a touch screen fires no drag events at all, so on a phone the
              relic in the pack could be read about and never worn. The button
              issues the SAME command the drop does and is gated by the same
              reason (`equipReason`/`unequipReason`, which the host answers out
              of the kernel), so there is one rule and two ways to reach it. */}
          {(() => {
            const at = inspect.at;
            const reason = at.kind === "backpack"
              ? equipReason(at.index, inspect.def.slot)
              : unequipReason(at.slot);
            const label = at.kind === "backpack" ? "Equip" : "Take off";
            return (
              <>
                <button
                  type="button"
                  className="hs-btn hs-btn--gold hm-hs__wearbtn"
                  disabled={reason !== null}
                  title={at.kind === "backpack"
                    ? `Wear the ${inspect.def.name} in the ${titleCase(inspect.def.slot)} socket`
                    : `Take the ${inspect.def.name} off and put it in the pack`}
                  onClick={() => {
                    props.onCommand(at.kind === "backpack"
                      ? { type: "EQUIP_ARTIFACT", hero: hero.id, backpackIndex: at.index, slot: inspect.def.slot }
                      : { type: "UNEQUIP_ARTIFACT", hero: hero.id, slot: at.slot });
                    setInspect(null);
                  }}
                >
                  {label}
                </button>
                {reason === null ? null : (
                  // The default lead is "Cannot build:", which is what this
                  // line says everywhere else it is used. A greyed button with
                  // the wrong sentence under it is worse than no sentence.
                  <ReasonLine content={content} reason={reason}
                    lead={at.kind === "backpack" ? "Cannot equip:" : "Cannot take off:"} />
                )}
              </>
            );
          })()}
          {/* The artifact half of a hero meeting.
              `GIVE_ARTIFACT` names a BAG index, so a worn relic is taken off
              first and then handed over - two commands for one gesture, which
              is what the classic's drag from a worn socket to the other hero's bag
              amounts to. The index the second command names is the bag's
              length as it stands, because `unequip` appends. */}
          {props.otherHero == null ? null : (() => {
            const to = props.otherHero;
            const at = inspect.at;
            const worn = at.kind === "slot";
            const reason = worn ? unequipReason(at.slot) : giveReason(at.index);
            return (
              <>
                <button
                  type="button"
                  className="hs-btn hs-btn--gold hm-hs__wearbtn"
                  style={{ marginTop: "8px" }}
                  disabled={reason !== null}
                  title={`Hand the ${inspect.def.name} to ${otherName}`}
                  onClick={() => {
                    const index = worn ? hero.backpack.length : at.index;
                    if (worn) props.onCommand({ type: "UNEQUIP_ARTIFACT", hero: hero.id, slot: at.slot });
                    props.onCommand({
                      type: "GIVE_ARTIFACT", fromHero: hero.id, toHero: to.id, backpackIndex: index,
                    });
                    setInspect(null);
                  }}
                >
                  {`Give to ${otherName}`}
                </button>
                {reason === null ? null : (
                  <ReasonLine content={content} reason={reason} lead="Cannot give:" />
                )}
              </>
            );
          })()}
          {combos.filter((c) => c.kind === "break"
            ? c.def.id === inspect.def.id
            : c.def.components.includes(inspect.def.id)).map((c) => (
              <button
                key={`${c.kind}:${c.def.id as string}`}
                type="button"
                className="hs-btn hs-btn--gold"
                style={{ marginTop: "10px", alignSelf: "flex-start" }}
                title={c.kind === "make"
                  ? `${c.def.name} - from ${String(c.def.components.length)} parts`
                  : `Take ${c.def.name} apart into ${String(c.def.components.length)} parts`}
                onClick={() => { props.onCommand(c.command); setInspect(null); }}
              >
                {c.kind === "make" ? `Assemble ${c.def.name}` : `Take ${c.def.name} apart`}
              </button>
            ))}
          <ReasonLine content={content} reason={null} />
        </ScreenDialog>
      )}

      {/* The creature card: the classic opens one on a right-click of any stack;
          here a click does, because the socket is a button already and a
          drag still starts on the press. */}
      {splitAsk === null ? null : (
        <SplitDialog
          content={content}
          armies={armies}
          from={{ army: splitAsk.from.army, slot: splitAsk.from.slot }}
          to={splitAsk.to}
          checkCommand={props.checkCommand}
          onSplit={(cmd) => { props.onCommand(cmd); dropCarry(); }}
          onClose={() => setSplitAsk(null)}
        />
      )}
      {unit === null ? null : (
        <ScreenDialog title={`${groupDigits(unit.count)} ${unit.count === 1 ? unit.def.name : unit.def.namePlural}`}
          onClose={() => setUnit(null)} width={phone.phone ? 340 : 520}>
          <div className="hs-row hm-hs__unitcard" style={{ gap: phone.phone ? 8 : 16, alignItems: "flex-start" }}>
            <div className="hs-slot hs-slot--card hm-hs__unitart" aria-hidden="true">
              <MaybeIcon atlasKey={creatureIcon(unit.def, unit.def.upgraded ? POSE_READY : POSE_IDLE)}
                size={phone.phone ? ART_ICON_LARGE : ART_ICON_LARGE * 2} title={unit.def.name} />
            </div>
            <div className="hs-col" style={{ gap: 6, flex: 1 }}>
              <div className="hs-tiny hs-muted">
                {`${titleCase(unit.def.faction as string)} · Tier ${String(unit.def.tier)}${unit.def.upgraded ? " · Upgraded" : ""}`}
              </div>
              <table className="hm-hs__unitstats" aria-label="Creature statistics">
                <tbody>
                  {creatureStatLines(unit.def).map((line) => (
                    <tr key={line.label}>
                      <th scope="row" className="hs-tiny hs-muted">{line.label}</th>
                      <td className="hs-tiny">{line.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="hs-tiny hs-muted" style={{ marginTop: 4 }}>Special abilities</div>
              {unit.def.abilities.length === 0
                ? <div className="hs-tiny">None</div>
                : (
                  <ul className="hm-hs__unitabilities">
                    {abilityPhrases(content, unit.def).map((line, i) => (
                      <li key={String(i)} className="hs-tiny" style={{ lineHeight: 1.45 }}>{line}</li>
                    ))}
                  </ul>
                )}
            </div>
          </div>
          {/* Send the stack across, without a drag.
              The mirror of the relic's "Give to" button and there for the same
              reason: a touch screen fires no drag events, so on a phone a
              stack could be read about and never moved. Both directions are
              here - the card knows which of the two armies it came from - and
              the destination socket is chosen the way a classic player would
              (thicken a stack of the same creature, else the first free
              socket) rather than being asked for. The kernel is still the one
              that says no. */}
          {(() => {
            const own = props.army ?? null;
            const other = props.otherArmy ?? null;
            if (own === null || other === null) return null;
            const fromArmy = unit.army === (own.id as string) ? own
              : unit.army === (other.id as string) ? other : null;
            if (fromArmy === null) return null;
            const toArmy = fromArmy.id === own.id ? other : own;
            const toName = toArmy.id === other.id ? otherSideName : ownSideName;
            const toSlot = receivingSlot(toArmy, unit.def.id);
            const cmd: GameCommand | null = toSlot === null ? null : {
              type: "TRANSFER_STACK",
              fromArmy: fromArmy.id, fromSlot: unit.slot,
              toArmy: toArmy.id, toSlot,
              count: unit.count,
            };
            // A full destination is its own sentence. Fabricating a
            // `SLOT_OCCUPIED` for it would print "that slot is taken" under a
            // button that never named a slot, which is the wrong sentence for
            // the right refusal - the same trap the relic button fell into
            // with "Cannot build:".
            const full = cmd === null;
            const reason = cmd === null ? null : props.checkCommand?.(cmd) ?? null;
            return (
              <>
                <button
                  type="button"
                  className="hs-btn hs-btn--gold hm-hs__wearbtn"
                  style={{ marginTop: "10px" }}
                  disabled={full || reason !== null}
                  title={`Send ${groupDigits(unit.count)} ${unit.count === 1 ? unit.def.name : unit.def.namePlural} to ${toName}`}
                  onClick={() => {
                    if (cmd !== null) props.onCommand(cmd);
                    setUnit(null);
                  }}
                >
                  {`Move to ${toName}`}
                </button>
                {full ? (
                  <div className="hs-tiny hs-muted" style={{ marginTop: "6px" }}>
                    {`${toName} has no socket free for ${unit.def.namePlural}.`}
                  </div>
                ) : reason === null ? null : (
                  <ReasonLine content={content} reason={reason} lead="Cannot move:" />
                )}
              </>
            );
          })()}
          {/* Pick the stack up instead. "Move" is the whole stack to a socket
              the player taps next - a swap, a merge or a transfer, whichever
              the kernel makes of the pair - and "Split" asks how many once the
              destination is known. The two-tap drag a touch screen can do. */}
          <div className="hs-row" style={{ gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="hs-btn hs-btn--gold hm-hs__wearbtn"
              title="Pick the stack up; the next socket you tap is where it goes"
              onClick={() => { setCarry({ army: unit.army, slot: unit.slot, count: null }); setCarryNote(null); setUnit(null); }}
            >
              Move…
            </button>
            <button
              type="button"
              className="hs-btn hs-btn--gold hm-hs__wearbtn"
              disabled={unit.count < 2}
              title={unit.count < 2 ? "One creature cannot be split" : "Choose a socket, then how many go there"}
              onClick={() => { setCarry({ army: unit.army, slot: unit.slot, count: "ask" }); setCarryNote(null); setUnit(null); }}
            >
              Split…
            </button>
          </div>
        </ScreenDialog>
      )}
      {skillInfo === null ? null : (
        <SkillPanel def={skillInfo.def} tier={skillInfo.tier} picks={skillInfo.picks}
          phone={phone.phone}
          onClose={() => { setSkillInfo(null); }} />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ skills

/**
 * One secondary skill: the medallion, and beside it the rank over the name.
 *
 * That is the reference's whole row - "Expert / Archery" beside a 44px icon,
 * eight of them two to a row - and it is all this plate draws. The rules text
 * and the three-rung mastery rail this plate used to carry are in the panel it
 * opens (`SkillPanel`), where there is room to print all three tiers; on the
 * sheet they made each skill a 170px card and the eight of them the largest
 * thing on a screen the reference keeps to a column.
 */
function SkillPlate(props: {
  readonly def: SecondarySkillDef | null;
  readonly skill: SecondarySkillState;
  readonly compact: boolean;
  readonly onOpen: (
    v: { def: SecondarySkillDef; tier: number; picks: readonly SubskillDefId[] },
  ) => void;
}): JSX.Element {
  const { compact, def, skill } = props;
  const name = def?.name ?? titleCase(skill.def as string);
  const tier = MASTERY_NAME[skill.level] ?? "";
  /* The branches taken, for the tooltip only. The row itself is the
     reference's - a 44px medallion and two lines - and a third line of text
     would make the eight of them the largest thing on the sheet again. */
  const branches = def === null ? [] : pickedSubskills(def, skill.picks ?? []);
  const held = branches.length === 0 ? "" : ` Branches: ${branches.map((b) => b.name).join(", ")}.`;

  return (
    <button
      type="button"
      className="hs-skill hm-hs__skillbtn"
      title={`${name} — ${tier}.${held} Click for what it does at every level.`}
      aria-label={`${name}, ${tier}. Open the skill's full effect.`}
      disabled={def === null}
      onClick={() => {
        if (def !== null) props.onOpen({ def, tier: skill.level, picks: skill.picks ?? [] });
      }}
    >
      <span className="hm-hs__skillrow">
        <span className="hs-skill__socket">
          <MaybeIcon
            atlasKey={def === null ? null : uiIconKey(def.art)}
            size={compact ? SKILL_ICON_COMPACT : SKILL_ICON}
            title={name}
            className="hm-relief__inlay"
          />
        </span>
        <span className="hm-hs__skilltext">
          <span className="hs-skill__level">
            <BitmapText face="serif" shadow scale={1}>{tier}</BitmapText>
          </span>
          <span className="hs-skill__name">
            <BitmapText face="serif" shadow scale={1}>{name}</BitmapText>
          </span>
        </span>
      </span>
    </button>
  );
}

/**
 * What a secondary skill does, at every level, over the sheet that opened it.
 *
 * A plaque has room for the tier the hero holds and a clause of why it matters;
 * the question a player actually has - what does the next level buy me, and how
 * much is this one worth - needs all three tiers side by side and the numbers
 * spelled out. So the panel resolves `levels[tier]` through the modifier
 * vocabulary rather than restating the prose: "+25% damage" is the reading, and
 * the sentence beside it is the reason.
 *
 * Some skills move no number at any level - Diplomacy changes what a wandering
 * army will agree to, not a stat - and an empty chip rail under three sentences
 * reads as a panel that failed to load. Those say so once, at the foot, and
 * carry no rail at all.
 *
 * Unlike `ScreenDialog` this one closes on a click outside as well as on
 * Escape: it is a thing you glance at, not a form with a half-filled slider to
 * lose.
 */
function SkillPanel(props: {
  readonly def: SecondarySkillDef;
  /** 1..3, the hero's own level in the skill. */
  readonly tier: number;
  /** The branches this hero took, so the panel can mark them. */
  readonly picks: readonly SubskillDefId[];
  /** Landscape phone: no hover, so a tap opens the branch's effect. */
  readonly phone?: boolean;
  readonly onClose: () => void;
}): JSX.Element {
  const { def, tier, picks, onClose } = props;
  const phone = props.phone === true;
  const [openSub, setOpenSub] = useState<string | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      /* Capture, and stopped here: the sheet behind this panel closes on the
         same key, and one Escape must dismiss one thing. */
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => { document.removeEventListener("keydown", onKey, true); };
  }, [onClose]);

  const ruleOnly = skillIsRuleOnly(def);
  const hasBranches = def.subskills.advanced.length > 0 || def.subskills.expert.length > 0;
  return (
    <div className="hs-scrim" role="presentation" onClick={onClose}>
      <div
        className="hs-dialog hm-hs__skillpanel"
        role="dialog"
        aria-modal="true"
        aria-label={`${def.name}, secondary skill`}
        onClick={(e) => { e.stopPropagation(); }}
      >
        <div className="hm-hs__skillhead">
          <span className="hs-skill__socket hm-hs__skillplate">
            <MaybeIcon atlasKey={uiIconKey(def.art)} size={phone ? 24 : SKILL_ICON_COMPACT}
              title={def.name} className="hm-relief__inlay" />
          </span>
          <span className="hm-hs__skillhead-text">
            <span className="hs-dialog__title">{def.name}</span>
            <span className="hm-hs__skillrank">
              {tier >= 1 && tier <= 3
                ? `Your hero has it at ${(MASTERY_NAME[tier] ?? "").toLowerCase()} level`
                : "Your hero has not learned this skill"}
            </span>
          </span>
        </div>
        <div className="hs-gilt-rule" />
        {phone && hasBranches ? (
          <div className="hs-tiny hs-muted hm-hs__subhint">Tap a branch to read its effect.</div>
        ) : null}
        <div className="hm-hs__tiers hs-scroll">
          {[1, 2, 3].map((t) => {
            const phrases = skillEffectPhrases(def, t);
            const rung = subskillRung(t);
            return (
              <div key={String(t)}
                className={`hm-hs__tier${t === tier ? " hm-hs__tier--now" : ""}`}>
                <span className="hm-hs__tiername">
                  {MASTERY_NAME[t] ?? ""}
                  {t === tier ? <span className="hm-hs__tiernow">This hero</span> : null}
                </span>
                <span className="hm-hs__tiertext">{def.description[t - 1] ?? ""}</span>
                {ruleOnly ? null : (
                  <span className="hm-hs__tierstats">
                    {phrases.length === 0
                      ? <span className="hm-hs__tiernone">No change to a stat at this level</span>
                      : phrases.map((p) => (
                        <span className="hm-hs__chip" key={p}>{p}</span>
                      ))}
                  </span>
                )}
                {/* The rung's branches, the one this hero took marked. Shown at
                    every tier, including the ones the hero has not reached, for
                    the same reason the tiers themselves are: the question is
                    what the next level buys. A title tooltip is hover-only, so
                    the chip is a button: tap (or click) reads the effect. */}
                {rung === null || def.subskills[rung].length === 0 ? null : (
                  <span className="hm-hs__tierstats">
                    {def.subskills[rung].map((sub) => {
                      const id = sub.id as string;
                      const open = openSub === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          className={`hm-hs__chip hm-hs__chip--sub${open ? " hm-hs__chip--open" : ""}`}
                          title={sub.description}
                          aria-expanded={open}
                          onClick={() => { setOpenSub(open ? null : id); }}
                        >
                          {picks.includes(sub.id) ? `\u2713 ${sub.name}` : sub.name}
                        </button>
                      );
                    })}
                    {def.subskills[rung].map((sub) => (
                      openSub === (sub.id as string)
                        ? <span key={`${sub.id as string}:desc`} className="hm-hs__subdesc">{sub.description}</span>
                        : null
                    ))}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {ruleOnly ? (
          <div className="hs-tiny hs-muted hm-hs__skillfoot">
            {`${def.name} changes a rule of play rather than a number, so it carries no stat bonus at any level.`}
          </div>
        ) : null}
        <div className="hs-dialog__foot">
          <button type="button" className="hs-btn hs-dialog__close hm-hs__skillclose" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- army

/**
 * A creature drawn at the rank's shared scale and seated on the socket's ground
 * line.
 *
 * The divisor comes from `srcH`, the *untrimmed* frame height, which every frame
 * of a faction sheet shares; taking it from the trimmed box - as fitting into a
 * square box does - gives a 281px Lancer a divisor of 3 and a 245px Angel a
 * divisor of 2, which is why the mounted stack read as the smallest figure in
 * the row. `anchorY` is the ground line inside the trimmed frame, so seating the
 * sprite by `h - anchorY` puts seven pairs of feet on one line whatever each
 * sprite's own crop was.
 */
function creatureSeat(
  atlas: Atlas, key: string | null, ground: number,
): { readonly style: CSSProperties; readonly drop: number } | null {
  const frame = key === null ? null : atlas.frame(key);
  if (frame === null) return null;
  const div = Math.max(1, Math.ceil(frame.srcH / ground));
  const sheet = atlas.manifest.sheets.find((s) => s.id === frame.sheet);
  /* The BOX is floored and the SAMPLE GRID is not, and mixing those up is what
     shipped here twice.
   *
   * Flooring the box is right and was the original fix: a stack drawn 93.5px
   * wide re-introduces the resample the whole-divisor rule exists to avoid,
   * and all seven stacks on the shipped 1920x1080 sheet were fractional
   * (93.5, 61.5, 72.5, 83.5).
   *
   * Flooring `background-size` with it was the overreach, and it is the more
   * damaging of the two. `sheet.w / div` is the ratio the sample grid IS: at
   * div 3 that is 1365.333, and 1365 makes the ratio 3.00073, which is exact
   * at the sheet's origin and a whole texel out by x=3900. Measured against a
   * Pillow nearest-neighbour downsample of the same frame, at seven Castle
   * stacks x two ground lines (2026-08-29):
   *
   *     ground 150 (1920x1080)   floored        exact
   *       spearwarden  div 3          30.65%         0.21%
   *       adept    div 3           0.20%         0.23%
   *     ground 96 (1366x768)
   *       angel    div 3          35.21%         0.88%
   *       skyclaw  div 3          49.18%         0.65%
   *       lancer div 3          33.75%         0.18%
   *       archer   div 4          31.12%         0.63%
   *
   * (Percentage of pixels that differ from the ideal downsample. ~0.5% is the
   * floor: both agree that closely at div 2, where 4096/2 is whole and the two
   * formulas are identical, so it is webp decode and alpha rounding.) Whether
   * a given frame was hit depended on where it happened to be packed, which is
   * why the Lampbearer looked fine and the Spearwarden beside it did not.
   *
   * See the long note on `spriteStyleBox` in `theme/atlas.ts`, which is the
   * same arithmetic and was already right. */
  const px = (n: number): string => `${String(Math.floor(n))}px`;
  const exact = (n: number): string => `${String(n / div)}px`;
  const style: CSSProperties = {
    width: px(frame.w / div),
    height: px(frame.h / div),
    backgroundPosition: `${exact(-frame.x)} ${exact(-frame.y)}`,
  };
  if (sheet !== undefined) {
    style.backgroundImage = `url(${atlas.sheetUrl(sheet.id)})`;
    style.backgroundSize = `${exact(sheet.w)} ${exact(sheet.h)}`;
  }
  return { style, drop: Math.floor((frame.h - frame.anchorY) / div) };
}

/**
 * Which authored pose a mustered stack stands in.
 *
 * Column 0 of a unit sheet is the resting stance and column 1 the readied one
 * (`POSE` in `@game/render`). An upgrade draws the readied pose, and the
 * reason is a content gap rather than a flourish: the shipped art has ONE
 * sheet per tier pair - `sunspire/07_radiant_high_radiant.png` is both - so
 * `creatureIcon` returns the same frame for Angel and High Radiant, and the sheet
 * drew four of its seven sockets as two identical pairs of pixels. There is no
 * High Radiant sprite to reach for. Standing the upgrade at the ready is the most
 * this screen can honestly say about the difference; a distinct sprite is a
 * content task and is written up in the report.
 */
const POSE_IDLE = 0;
const POSE_READY = 1;

function HeroArmyRow(props: {
  readonly content: ContentRegistry;
  readonly army: ArmyState | null;
  readonly label: string;
  readonly ground: number;
  /** The plinth band at the socket's foot, which the count plaque sits in. */
  readonly plinth: number;
  /** Whole magnification of the count numeral; see `ARMY_COUNT_SCALE`. */
  readonly countScale: number;
  readonly dragSource: { readonly army: string; readonly slot: number } | null;
  readonly dropTarget: { readonly army: string; readonly slot: number } | null;
  /** The stack in hand, if any: its socket dims and every other socket opens. */
  readonly carry: Carry | null;
  readonly onDragStart: (armyId: string, slot: number, single: boolean) => void;
  readonly onDragEnter: (armyId: string, slot: number) => void;
  readonly onDrop: (armyId: string, slot: number) => void;
  /** A tap on any socket; the sheet decides whether that inspects or drops. */
  readonly onTap: (armyId: string, slot: number, def: CreatureDef | null, count: number) => void;
}): JSX.Element {
  const atlas = useAtlas();
  const slots = props.army?.slots ?? [null, null, null, null, null, null, null];
  const id = (props.army?.id ?? "") as string;
  return (
    <div className="hs-armyrow hm-hs__armyrow">
      <div className="hs-armyrow__head">
        <span className="hs-clip">{props.label}</span>
      </div>
      <div className="hs-armyrow__slots">
        {slots.map((slot, i) => {
          const def = slot === null ? null : props.content.creatures.get(slot.def) ?? null;
          const seat = def === null
            ? null
            : creatureSeat(
              atlas,
              creatureIcon(def, def.upgraded ? POSE_READY : POSE_IDLE),
              props.ground,
            );
          const isTarget = props.dropTarget != null
            && props.dropTarget.army === id && props.dropTarget.slot === i;
          const carried = props.carry !== null && props.carry.army === id && props.carry.slot === i;
          const open = props.carry !== null && !carried;
          const dragging = carried || (props.dragSource != null
            && props.dragSource.army === id && props.dragSource.slot === i);
          const label = def === null
            ? "Empty"
            : `${groupDigits(slot?.count ?? 0)} ${def.namePlural}`;
          const cls = [
            "hs-slot", "hm-hs__army",
            slot === null ? "hs-slot--empty" : "",
            isTarget ? "hs-slot--drop" : "",
            dragging ? "hs-slot--dragging" : "",
            open ? "hs-slot--open" : "",
          ].filter((c) => c.length > 0).join(" ");
          return (
            <button
              key={`${id}:${String(i)}`}
              type="button"
              className={cls}
              title={label}
              aria-label={label}
              draggable={slot !== null}
              onClick={() => { props.onTap(id, i, def, slot?.count ?? 0); }}
              onDragStart={(e) => props.onDragStart(id, i, e.shiftKey)}
              onDragOver={(e) => { e.preventDefault(); }}
              onDragEnter={() => props.onDragEnter(id, i)}
              onDrop={(e) => { e.preventDefault(); props.onDrop(id, i); }}
            >
              {seat === null ? null : (
                <>
                  <span className="hm-hs__armyshadow" aria-hidden="true" />
                  <span
                    className="hm-hs__armyart"
                    style={{ ...seat.style, bottom: `${String(props.plinth - seat.drop)}px` }}
                    aria-hidden="true"
                  />
                </>
              )}
              {/* The plinth is drawn whether the socket is filled or not: an
                  empty socket that lost its floor reads as a different kind of
                  hole from the six beside it. */}
              <span className="hm-hs__plinth" aria-hidden="true" />
              {slot === null ? null : (
                <span className="hs-slot__count">
                  {/* One of the few strings in the build with no plate under
                      it - `.hs-slot__count` paints no background, by design -
                      so it is one of the few that has to carry its own
                      keyline. With the halo off, the ring of pixels around
                      `58` over the Angel measured mean luminance 253 against
                      87 with it on. */}
                  <BitmapText scale={props.countScale} outline>{compactCount(slot.count)}</BitmapText>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Where a relic is lying: in the pack at an index, or worn in a socket. */
type ArtifactWhere =
  | { readonly kind: "backpack"; readonly index: number }
  | { readonly kind: "slot"; readonly slot: string };

